import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import bgImage from "assets/images/garrafeira1.jpg";

export default function Login() {
  const { showAlert } = useAlert();
  const { login, isAuthenticated, userData } = useAuth();

  const navigate = useNavigate();

  const [form, setForm] = useState({ username: "", pwd: "" });
  const [submitting, setSubmitting] = useState(false);

  // dialog "esqueci-me da palavra-passe"
  const [forgotOpen, setForgotOpen] = useState(false);
  const [email, setEmail] = useState("@jfbebidas.com");
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate("/dashboard/cv-dashboard", { replace: true });
  }, [isAuthenticated, navigate]);

  async function handleSignIn(e) {
    e.preventDefault();
    if (!form.username || !form.pwd) {
      showAlert("Indique utilizador e palavra-passe.", "warning");
      return;
    }
    setSubmitting(true);

    const [payload, err, meta] = await handleApi(
      api.post("Auth/Login", { username: form.username, pwd: form.pwd })
    );

    setSubmitting(false);

    // erro de transporte (HTTP/network)
    if (err) return showAlert(err, "error");

    // erro lógico devolvido pela API (ex.: Utilizador inativo / credenciais inválidas)
    if (!meta?.success) {
      return showAlert(meta?.message || "Não foi possível iniciar sessão.", "error");
    }

    // Esperado: { token, utilizador: { … } } em payload
    const user = payload?.utilizador;
    const token = payload?.token;
    if (!user || !token) {
      return showAlert("Resposta inesperada do servidor.", "error");
    }

    // guarda sessão e segue
    login(user, token);
    showAlert(meta?.message || "Sessão iniciada.", "success");
    navigate("/dashboard", { replace: true });
  }

  async function handleForgot() {
    if (!email || !email.includes("@")) {
      showAlert("Indique um e-mail válido.", "warning");
      return;
    }
    setResetting(true);
    const [_, err, meta] = await handleApi(api.post("Auth/ResetPassword", { email }));
    setResetting(false);

    if (err) return showAlert(err, "error");
    showAlert(
      meta?.message || "Foi enviada uma palavra-passe provisória para o e-mail indicado.",
      "success"
    );
    setForgotOpen(false);
  }

  return (
    <MDBox
      position="relative"
      minHeight="100vh"
      width="100%"
      display="grid"
      sx={{
        placeItems: "center",
        // overlay branco suave por cima da imagem
        backgroundImage: `linear-gradient(rgba(255,255,255,.55), rgba(255,255,255,.55)), url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        // opcional: um toque de blur para suavizar ainda mais a imagem
        // backdropFilter só afeta conteúdos de trás; aqui usamos um leve brightness na camada
        backgroundBlendMode: "normal",
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 520, mx: 2 }}>
        <MDBox
          variant="gradient"
          borderRadius="lg"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
          sx={({ palette, functions }) => ({
            background: functions.linearGradient(palette.corbase1.main, palette.corbase1.focus),
            boxShadow: "none", // evita sombra azul do "coloredShadow"
          })}
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Iniciar sessão
          </MDTypography>
          <Grid container spacing={0} justifyContent="center" sx={{ mt: 1, mb: 1 }} />
        </MDBox>

        <MDBox pt={3} pb={3} px={3} component="form" onSubmit={handleSignIn}>
          <MDBox mb={2}>
            <MDInput
              type="text"
              label="Utilizador"
              fullWidth
              value={form.username}
              inputProps={{
                autoCapitalize: "none",
                autoCorrect: "off",
                spellCheck: false,
              }}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  username: e.target.value.toLowerCase(),
                }))
              }
            />
          </MDBox>

          <MDBox mb={1.5}>
            <MDInput
              type="password"
              label="Palavra-passe"
              fullWidth
              defaultValue={form.pwd}
              onChange={(e) => setForm((p) => ({ ...p, pwd: e.target.value }))}
            />
          </MDBox>

          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <MDButton
              variant="text"
              color="info"
              onClick={() => setForgotOpen(true)}
              sx={{ textTransform: "none", paddingLeft: 0 }}
            >
              Esqueci-me da palavra-passe
            </MDButton>
          </MDBox>

          <MDBox mt={2} mb={1}>
            <MDButton type="submit" variant="gradient" color="dark" fullWidth disabled={submitting}>
              {submitting ? "A entrar..." : "Entrar"}
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>

      {/* Dialog de reset password */}
      <Dialog open={forgotOpen} onClose={() => setForgotOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Recuperar palavra-passe</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text" sx={{ display: "block", mb: 1 }}>
            Introduza o e-mail associado à sua conta. Se existir, enviaremos uma palavra-passe
            provisória.
          </MDTypography>
          <TextField
            autoFocus
            fullWidth
            type="email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="standard"
          />
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={() => setForgotOpen(false)}>
            Cancelar
          </MDButton>
          <MDButton color="info" onClick={handleForgot} disabled={resetting}>
            {resetting ? "A enviar..." : "Enviar"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
